'use client'

import type { FC } from 'react'
import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { Brain, BookOpen, Puzzle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  assessmentCategories,
  getTotalAssessmentCount,
  type AssessmentCategory,
  type AssessmentSubcategory,
} from '@/lib/assessments'

/**
 * TeamAssessments component
 * -------------------------------------------------------------
 * Responsive three-column grid with proper semantic HTML.
 * Data-driven from lib/assessments.ts for easy maintenance.
 */

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  brain: Brain,
  book: BookOpen,
  puzzle: Puzzle,
}

// Helper to render a semantically correct assessment list
const renderAssessmentList = (
  items: string[],
  listId: string,
  max?: number,
) => {
  const displayItems = max ? items.slice(0, max) : items
  const hasMore = max && items.length > max

  return (
    <>
      <ul
        id={listId}
        className="space-y-0 divide-y divide-transparent"
        role="list"
      >
        {displayItems.map((title, index) => (
          <li
            key={`${listId}-${index}`}
            className="text-xs sm:text-sm text-gray-700 leading-snug px-2 py-1.5 even:bg-gray-50 rounded transition-colors hover:bg-gray-100"
          >
            {title}
          </li>
        ))}
      </ul>
      {hasMore && (
        <p className="text-[11px] sm:text-xs text-gray-500 mt-2 italic">
          + {items.length - max} more assessment
          {items.length - max !== 1 ? 's' : ''} available
        </p>
      )}
    </>
  )
}

// Component to render a single subcategory
const SubcategorySection: FC<{
  subcategory: AssessmentSubcategory
  categoryId: string
  index: number
  maxItems?: number
}> = ({ subcategory, categoryId, index, maxItems }) => {
  const subcategoryId = `${categoryId}-${index}`
  const headingId = `${subcategoryId}-heading`
  const listId = `${subcategoryId}-list`

  return (
    <section
      aria-labelledby={headingId}
      className={index > 0 ? 'mt-4 sm:mt-6' : ''}
    >
      <h4
        id={headingId}
        className="text-sm sm:text-base font-semibold text-gray-700 mb-1.5 sm:mb-2"
      >
        {subcategory.title}
      </h4>
      {subcategory.description && (
        <p className="text-[11px] sm:text-xs text-gray-600 mb-2 italic">
          {subcategory.description}
        </p>
      )}
      {renderAssessmentList(subcategory.items, listId, maxItems)}
    </section>
  )
}

// Component to render a single assessment category (article)
const CategoryArticle: FC<{
  category: AssessmentCategory
  maxItemsFirstSubcategory?: number
}> = ({ category, maxItemsFirstSubcategory }) => {
  const Icon = iconMap[category.icon] || Puzzle
  const headingId = `${category.id}-heading`

  return (
    <article aria-labelledby={headingId}>
      <header className="mb-3 sm:mb-4">
        <h3
          id={headingId}
          className="flex items-center gap-2 text-lg sm:text-xl font-semibold text-gray-800"
        >
          <Icon
            className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 flex-shrink-0"
            aria-hidden="true"
          />
          <span>{category.title}</span>
        </h3>
        {category.description && (
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            {category.description}
          </p>
        )}
      </header>

      {category.subcategories.map((subcategory, index) => (
        <SubcategorySection
          key={`${category.id}-sub-${index}`}
          subcategory={subcategory}
          categoryId={category.id}
          index={index}
          maxItems={index === 0 ? maxItemsFirstSubcategory : undefined}
        />
      ))}
    </article>
  )
}

const TeamAssessments: FC = () => {
  const totalCount = getTotalAssessmentCount()
  const router = useRouter()

  const handleBookAppointment = useCallback(() => {
    router.push('/book')
  }, [router])

  return (
    <section
      className="py-10 sm:py-12 md:py-16 bg-white"
      aria-labelledby="assessments-heading"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-8 sm:mb-10">
          <h2
            id="assessments-heading"
            className="text-[26px] sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 leading-tight"
          >
            Assessments We Offer
          </h2>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            Each evaluation is administered by fellowship-trained clinicians to
            give you clear, actionable insights into your mental health and
            cognitive functioning.
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mt-1.5 sm:mt-2">
            {totalCount} specialized assessments available
          </p>
        </header>

        {/* Three-column responsive grid with semantic structure */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7 md:gap-8 text-left items-start"
          role="group"
          aria-label="Assessment categories"
        >
          {assessmentCategories.map((category, index) => (
            <CategoryArticle
              key={category.id}
              category={category}
              maxItemsFirstSubcategory={
                index === 0 && category.subcategories[0]?.items.length > 3
                  ? 3
                  : undefined
              }
            />
          ))}
        </div>

        {/* Disclaimer box & CTA */}
        <footer className="mt-8 sm:mt-10 md:mt-12 space-y-5 sm:space-y-6">
          <aside
            className="bg-red-500/90 text-white rounded-lg p-4 sm:p-5 max-w-2xl mx-auto shadow-md"
            role="note"
            aria-label="Important disclaimer"
          >
            <p className="text-xs sm:text-sm md:text-base leading-relaxed">
              <strong className="font-semibold">Important:</strong> Please
              don&apos;t attempt these assessments on your own. Meet a licensed
              therapist who can guide you through the process and interpret the
              results accurately for your specific needs.
            </p>
          </aside>

          <div className="text-center">
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-lg shadow-sm transition-colors"
              onClick={handleBookAppointment}
              aria-label="Book an assessment appointment"
            >
              Book an Appointment
            </Button>
          </div>
        </footer>
      </div>
    </section>
  )
}

export default TeamAssessments
